import type { Sales, SalesData } from "~/layers/media/types/Sales";

/** Сколько акций показываем в блоке «Еще акции» (IT-1377). */
export const SIMILAR_SALES_LIMIT = 3;

/** Сколько акций разбираем в поисках похожих: больше их на сайте не бывает. */
const CANDIDATES_LIMIT = 50;

interface SimilarSalesOptions {
  /** Слаг текущей акции — её саму в подборку не берём. */
  slug: string;
  /** Проекты текущей акции: чем больше их совпало, тем выше акция в выдаче. */
  projectIds: number[];
  /** Сколько акций нужно добрать. */
  limit: number;
}

/**
 * Подборка других акций для детальной страницы.
 *
 * Порядок: сначала действующие (не прошёл `deadline_date`), среди них — похожие на
 * текущую (совпадает больше проектов), внутри равных — свежие. Истёкшие не выбрасываем,
 * а опускаем вниз: со сроком в админке работают не всегда, и в разделе «Акции» такие
 * записи всё равно висят — лучше показать их, чем пустой блок.
 */
export async function useSimilarSales(options: SimilarSalesOptions) {
  const { find } = useStrapi();
  const today = new Date().toISOString().slice(0, 10);
  const selectedProjects = new Set(options.projectIds);

  const isActive = (sale: SalesData) =>
    Number(!sale.attributes?.deadline_date || sale.attributes.deadline_date >= today);

  const matchedProjects = (sale: SalesData) =>
    (sale.attributes?.projects?.data ?? []).filter((project) => selectedProjects.has(project.id)).length;

  // Ранжируем внутри useAsyncData: в payload страницы уезжают только отобранные акции,
  // а не весь список кандидатов с обложками.
  const { data } = await useAsyncData(`sales-similar-${options.slug}`, async () => {
    const response = await find<Sales>("sales", {
      fields: ["title", "slug", "description", "publication_date", "deadline_date"],
      filters: {
        slug: { $ne: options.slug },
      },
      populate: {
        cover: "*",
        projects: { fields: ["title"] },
      },
      sort: "publication_date:desc",
      pagination: { pageSize: CANDIDATES_LIMIT },
    }).catch(() => null);

    return [...(response?.data ?? [])]
      .sort((a, b) => isActive(b) - isActive(a) || matchedProjects(b) - matchedProjects(a))
      .slice(0, options.limit);
  });

  return computed<SalesData[]>(() => data.value ?? []);
}
